'use client';

import { useCallback, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { PageRoute, PageRoutes } from '../constants/constants';

import useCurrentMarketMarkPrice from './useCurrentMarketMarkPrice';
import {
	cleanTickerSymbol,
	formatMarketPrice,
} from '../constants/marketDecimalConfig';
import useFeatureAnnouncementGlobalState from 'src/components/FeatureAnnouncementTrigger/hooks/useFeatureAnnouncementGlobalState';

const useDocumentTitle = () => {
	const pathname = usePathname();
	const currentMarketName = useDriftStore((s) => s.selectedMarket.name);
	const currentRoute = useDriftStore((s) => s.currentPageRoute);

	const markPrice = useCurrentMarketMarkPrice();

	const [{ unseenCount }] = useFeatureAnnouncementGlobalState();

	const setTitleToRender = useCallback(
		(newTitle: string) => {
			if (!document?.title) return;

			if (unseenCount > 0) {
				document.title = `(${unseenCount}) ${newTitle}`;
			} else {
				document.title = newTitle;
			}
		},
		[unseenCount]
	);

	useEffect(() => {
		// check which route
		if (currentRoute) {
			if (PageRoutes.includes(currentRoute as PageRoute)) {
				switch (currentRoute as PageRoute) {
					case PageRoute.disclaimer:
						setTitleToRender('Disclaimer — Drift');
						break;
					case PageRoute.privacy:
						setTitleToRender('Privacy Policy — Drift');
						break;
					case PageRoute.terms:
						setTitleToRender('Terms — Drift');
						break;
					case PageRoute.portfolio:
						setTitleToRender('Portfolio — Drift');
						break;
					case PageRoute.funding:
						setTitleToRender('Funding — Drift');
						break;
					case PageRoute.leaderboards: {
						// Extract leaderboard type from pathname (/leaderboard/[type])
						const leaderboardMatch = pathname?.match(/\/leaderboard\/(\w+)/);
						const leaderboardType = leaderboardMatch
							? leaderboardMatch[1]
							: null;

						if (leaderboardType) {
							// Special case for USD1 to ensure it displays as all caps
							const formattedType =
								leaderboardType === 'usd1'
									? 'USD1'
									: leaderboardType.charAt(0).toUpperCase() +
										leaderboardType.slice(1);
							setTitleToRender(`Leaderboard - ${formattedType} — Drift`);
						} else {
							setTitleToRender('Leaderboard — Drift');
						}
						break;
					}
					case PageRoute.redeem:
						setTitleToRender('Redeem - Drift');
						break;
					case PageRoute.borrowLend:
						setTitleToRender('Borrow/Lend - Drift');
						break;
					case PageRoute.stake:
						setTitleToRender('Stake - Drift');
						break;
					case PageRoute.bridge:
						setTitleToRender(`Bridge - Drift`);
						break;
					case PageRoute.swap:
						setTitleToRender(`Swap - Drift`);
						break;
					case PageRoute.BAL:
						setTitleToRender(`Backstop AMM Liquidity - Drift`);
						break;
					case PageRoute.elections:
						setTitleToRender(`Election Center - Drift`);
						break;
					case PageRoute.bet:
						setTitleToRender(`BET - Drift`);
						break;
					case PageRoute.vaults:
						setTitleToRender(`Vaults - Drift`);
						break;
					case PageRoute.stats:
						setTitleToRender(`Stats - Drift`);
						break;
					default:
						setTitleToRender('Drift');
				}

				return;
			}
		}

		// otherwise we're at the default route
		if (markPrice && markPrice.gtZero()) {
			const cleanMarketName = currentMarketName
				? cleanTickerSymbol(currentMarketName)
				: '';
			const baseSymbol = cleanMarketName.replace('-SPOT', ''); // Get base symbol for decimal config
			const formattedPrice = formatMarketPrice(
				markPrice.toNum(),
				baseSymbol,
				true
			);
			setTitleToRender(`$${formattedPrice} | ${cleanMarketName} | Drift`);

			return;
		}

		// otherwise just use the default title
		setTitleToRender('Drift');
	}, [currentRoute, markPrice, pathname, setTitleToRender]);
};

export default useDocumentTitle;
